import { FC, useMemo, useState } from "react"
import cx from "classnames"
import { SerializedError } from "@reduxjs/toolkit"
import { FetchBaseQueryError } from "@reduxjs/toolkit/query"
import { useParams } from "react-router-dom"
import { usePageTitle } from "@/hooks/usePageTitle"
import { useAuth } from "@/hooks/useAuth"
import AddIcon from "@/components/icons/AddIcon"
import Button from "@/components/Button"
import EmptyList from "@/components/EmptyList"
import ErrorMessage from "@/components/ErrorMessage"
import Loader from "@/components/Loader"
import Modal from "@/components/Modal"
import OutlineButton from "@/components/OutlineButton"
import SearchIcon from "@/assets/search.svg"
import TextField from "@/components/TextField"
import {
  useAddDomainsToFolderMutation,
  useEnableInboxingDomainMutation,
  useGetInboxingDomainsQuery,
  useSyncInboxingDomainsMutation,
} from "@/redux/services/domainsApi"
import { useOrganizationPermissions } from "@/hooks/useOrganizationPermissions"
import { SANDMAN_PROVIDER_OPTIONS } from "@/utils/sandman"
import type {
  Domain,
  DomainPlacementState,
  InboxingEntitlements,
} from "@/types/entities/domain"
import type { EmailAddress } from "@/types/entities/email-address"

type Props = {
  className?: string
}

type AlertTone = "error" | "warning" | "success" | "info"

type AlertDetails = {
  title: string
  message: string
  tone?: AlertTone
}

type PendingAction = { type: "sync" } | { type: "enable" } | null

const EMPTY_MAILBOXES: EmailAddress[] = []

const BLOCKED_BILLING_STATUSES = new Set([
  "blocked",
  "canceled",
  "cancelled",
  "disabled",
  "expired",
  "inactive",
  "past_due",
  "requires_payment",
  "unpaid",
])

const ERROR_DETAILS: Record<string, { title: string; message: string }> = {
  BILLING_REQUIRED: {
    title: "DreamSender billing required",
    message:
      "Enable DreamSender billing in Inboxing before importing or enabling domains.",
  },
  ENTITLEMENT_LIMIT: {
    title: "Domain allowance reached",
    message:
      "Your current DreamSender allowance is full. Increase the allowance or remove another domain first.",
  },
  DS_ADDON_DISABLED: {
    title: "DreamSender add-on disabled",
    message: "Turn on the DreamSender add-on in Inboxing billing to continue.",
  },
  SUBSCRIPTION_PAST_DUE: {
    title: "Subscription past due",
    message: "Update billing in Inboxing before enabling more domains.",
  },
}

const pluralize = (count: number, singular: string, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`

const normalizedValue = (value?: unknown) =>
  value === undefined || value === null
    ? ""
    : String(value).trim().toLowerCase()

const humanize = (value?: unknown, fallback = "Unknown") => {
  if (value === undefined || value === null || value === "") return fallback

  return String(value)
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

const testIdSegment = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")

const domainIsEnabledForDreamSender = (domain: Domain) => {
  if (domain.dsEnabledAt && !domain.dsDisabledAt) return true
  if (domain.dsEnabled === true && !domain.dsDisabledAt) return true

  const dreamSenderStatus = normalizedValue(domain.dreamSenderStatus)
  return (
    !domain.dsDisabledAt &&
    ["enabled", "active", "ready"].includes(dreamSenderStatus)
  )
}

const mailboxCount = (domain: Domain) =>
  domain.mailboxes?.length ?? domain.mailboxCount ?? 0

const domainDailyCapacity = (domain: Domain) => {
  const capacity =
    domain.availableDailyCapacity ??
    domain.safeDailyCapacity ??
    domain.mailboxDailyCapacity ??
    domain.effectiveDomainDailyCap ??
    domain.domainDailyCap ??
    domain.autoDomainDailyCap
  return typeof capacity === "number" ? Math.max(0, capacity) : undefined
}

const mailboxStatus = (mailbox: EmailAddress) => {
  if (mailbox.role === "catch_all") return "Fallback inbox"
  if (mailbox.externalDeletedAt || mailbox.warmupStage === "RETIRED") {
    return "Retired"
  }
  if (mailbox.pausedAt) return "Paused"
  if (mailbox.graphSendReady && mailbox.sendEligible !== false) return "Ready"
  return humanize(mailbox.warmupStage || mailbox.graphSendStatus, "Preparing")
}

const mailboxDailyCapacity = (mailbox: EmailAddress) => {
  if (mailbox.role === "catch_all") return "No sending"
  const cap = mailbox.currentDailyCap ?? mailbox.dailyLimit ?? 0
  return cap > 0 ? `${cap}/day` : "Paused"
}

const entitlementRemaining = (entitlements?: InboxingEntitlements) => {
  if (!entitlements) return undefined
  if (typeof entitlements.domainsRemaining === "number") {
    return entitlements.domainsRemaining
  }
  if (
    typeof entitlements.domainLimit === "number" &&
    typeof entitlements.domainsEnabled === "number"
  ) {
    return Math.max(entitlements.domainLimit - entitlements.domainsEnabled, 0)
  }
  if (
    typeof entitlements.domainLimit === "number" &&
    typeof entitlements.domainsUsed === "number"
  ) {
    return Math.max(entitlements.domainLimit - entitlements.domainsUsed, 0)
  }
  return undefined
}

const entitlementUsage = (entitlements?: InboxingEntitlements) => {
  if (!entitlements || entitlements.source === "unavailable") {
    return "Allowance unavailable"
  }

  const remaining = entitlementRemaining(entitlements)
  if (
    typeof entitlements.domainLimit === "number" &&
    typeof remaining === "number"
  ) {
    const enabled =
      entitlements.domainsEnabled ??
      entitlements.domainsUsed ??
      Math.max(entitlements.domainLimit - remaining, 0)
    return `${enabled} / ${entitlements.domainLimit} enabled · ${remaining} remaining`
  }
  if (typeof remaining === "number") return `${remaining} remaining`
  if (typeof entitlements.domainLimit === "number") {
    return `${entitlements.domainLimit} domains included`
  }
  return "Allowance unavailable"
}

const entitlementBlock = (entitlements?: InboxingEntitlements) => {
  if (!entitlements) return undefined

  const code = normalizedValue(
    entitlements.code ||
      entitlements.blockedCode ||
      entitlements.blockCode ||
      entitlements.reason,
  ).toUpperCase()
  const billingStatus = normalizedValue(entitlements.billingStatus)
  const remaining = entitlementRemaining(entitlements)

  if (
    entitlements.blocked ||
    entitlements.isBlocked ||
    entitlements.canEnableDomains === false ||
    BLOCKED_BILLING_STATUSES.has(billingStatus)
  ) {
    const details = ERROR_DETAILS[code] || ERROR_DETAILS.BILLING_REQUIRED
    return {
      title: details.title,
      message: entitlements.message || details.message,
      tone: "error" as const,
    }
  }

  if (remaining === 0) {
    return {
      title: ERROR_DETAILS.ENTITLEMENT_LIMIT.title,
      message: ERROR_DETAILS.ENTITLEMENT_LIMIT.message,
      tone: "warning" as const,
    }
  }

  return undefined
}

const describeApiError = (
  error: FetchBaseQueryError | SerializedError,
): AlertDetails => {
  if ("data" in error && error.data && typeof error.data === "object") {
    const data = error.data as Record<string, any>
    const code = normalizedValue(
      data.error?.code || data.code || data.blockedCode || data.reason,
    ).toUpperCase()
    const details = ERROR_DETAILS[code]
    return {
      title: details?.title || "Action failed",
      message:
        data.message || data.error?.message || details?.message || "Try again.",
      tone: "error",
    }
  }

  return {
    title: "Action failed",
    message: "message" in error && error.message ? error.message : "Try again.",
    tone: "error",
  }
}

const Alert = ({ details }: { details: AlertDetails }) => {
  const toneClass = {
    error: "border-error/30 bg-error/10 text-error",
    warning: "border-warning/40 bg-warning/20 text-black",
    success: "border-positive/30 bg-positive/10 text-positive",
    info: "border-info/30 bg-info/10 text-info",
  }[details.tone || "info"]

  return (
    <div
      className={cx("rounded-md border px-4 py-3 text-sm", toneClass)}
      data-testid="inboxing-alert"
    >
      <div className="font-semibold">{details.title}</div>
      <div className="mt-1 opacity-80">{details.message}</div>
    </div>
  )
}

const StatusPill = ({ children }: { children: React.ReactNode }) => (
  <span className="rounded-full bg-positive/10 px-2 py-1 text-xs font-medium text-positive">
    {children}
  </span>
)

const PLACEMENT_BADGE_STATES = new Set<DomainPlacementState>([
  "burned",
  "resting",
])

const placementBadgeClass = (state: DomainPlacementState) =>
  state === "burned" ? "bg-error/10 text-error" : "bg-warning/20 text-black"

const domainHasBurnedPlacement = (domain: Domain) =>
  SANDMAN_PROVIDER_OPTIONS.some(
    ({ key }) => domain.placement?.[key] === "burned",
  )

const PlacementBadge = ({
  provider,
  state,
  domainId,
}: {
  provider: string
  state: DomainPlacementState
  domainId: string
}) => (
  <span
    className={cx(
      "rounded-full px-2 py-1 text-xs font-medium",
      placementBadgeClass(state),
    )}
    data-testid={`placement-badge-${domainId}-${provider}-${state}`}
  >
    {humanize(provider)} {humanize(state)}
  </span>
)

type AddDomainModalProps = {
  open: boolean
  domains: Domain[]
  entitlements?: InboxingEntitlements
  selectedDomainIds: string[]
  search: string
  selectedTags: string[]
  loading: boolean
  syncLoading: boolean
  onClose: () => void
  onConfirm: () => void
  onSearchChange: (value: string) => void
  onSelectedDomainIdsChange: (value: string[]) => void
  onTagToggle: (tag: string) => void
  onRefresh: () => void
  showAdminPull: boolean
  adminPullOrgId: string
  onAdminPullOrgIdChange: (value: string) => void
}

const AddDomainModal = ({
  open,
  domains,
  entitlements,
  selectedDomainIds,
  search,
  selectedTags,
  loading,
  syncLoading,
  onClose,
  onConfirm,
  onSearchChange,
  onSelectedDomainIdsChange,
  onTagToggle,
  onRefresh,
  showAdminPull,
  adminPullOrgId,
  onAdminPullOrgIdChange,
}: AddDomainModalProps) => {
  const remaining = entitlementRemaining(entitlements)
  const selectedDomains = domains.filter((domain) =>
    selectedDomainIds.includes(domain._id),
  )
  const newEnableCount = selectedDomains.filter(
    (domain) => !domainIsEnabledForDreamSender(domain),
  ).length
  const block = newEnableCount > 0 ? entitlementBlock(entitlements) : undefined
  const selectedExceedsAllowance =
    typeof remaining === "number" && newEnableCount > remaining
  const allTags = useMemo(
    () =>
      [...new Set(domains.flatMap((domain) => domain.tags || []))].sort(
        (a, b) => a.localeCompare(b),
      ),
    [domains],
  )
  const filteredDomains = useMemo(() => {
    const term = search.toLowerCase().trim()
    return domains.filter((domain) => {
      const values = [domain.name, ...(domain.tags || [])].map((value) =>
        value.toLowerCase(),
      )
      const matchesSearch =
        !term || values.some((value) => value.includes(term))
      const matchesTags =
        !selectedTags.length ||
        selectedTags.every((tag) => domain.tags?.includes(tag))
      return matchesSearch && matchesTags
    })
  }, [domains, search, selectedTags])
  const canConfirm =
    selectedDomainIds.length > 0 &&
    !loading &&
    !block &&
    !selectedExceedsAllowance

  const setSelected = (domainId: string, selected: boolean) => {
    onSelectedDomainIdsChange(
      selected
        ? [...new Set([...selectedDomainIds, domainId])]
        : selectedDomainIds.filter((id) => id !== domainId),
    )
  }

  return (
    <Modal
      open={open}
      title="Add Domain"
      onClose={onClose}
      boxClassName="max-w-3xl"
      contentClassName="max-h-[70vh]"
      actions={
        <div className="flex flex-wrap justify-end gap-2">
          <OutlineButton
            type="button"
            className="btn-sm bg-white text-black"
            disabled={loading}
            onClick={onClose}
          >
            Cancel
          </OutlineButton>
          <Button
            type="button"
            className="btn-sm"
            disabled={!canConfirm}
            loading={loading}
            onClick={onConfirm}
            data-testid="confirm-enable-domains"
          >
            {loading
              ? "Adding..."
              : `Add ${pluralize(selectedDomainIds.length, "domain")}`}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div
          className="rounded-md border border-outline bg-bg-default px-4 py-3 text-sm"
          data-testid="inboxing-entitlements"
        >
          <div className="font-semibold">DreamSender allowance</div>
          <div className="mt-1 text-text-light">
            {entitlementUsage(entitlements)}
            {typeof remaining === "number" &&
              ` · You can select up to ${remaining} more`}
          </div>
        </div>

        {block && <Alert details={block} />}
        {selectedExceedsAllowance && (
          <Alert
            details={{
              title: "Too many domains selected",
              message: `Select ${remaining} or fewer domains for this allowance.`,
              tone: "warning",
            }}
          />
        )}

        {showAdminPull && (
          <div className="rounded-md border border-outline bg-bg-default px-4 py-3">
            <div className="text-sm font-semibold">Admin: pull from Inboxing org</div>
            <div className="mt-1 text-xs text-text-light">
              Paste an Inboxing organization ID to pull that org&apos;s active
              domains, then click Refresh. Leave blank to refresh your own org.
            </div>
            <TextField
              value={adminPullOrgId}
              onChange={({ target: { value } }) =>
                onAdminPullOrgIdChange(value)
              }
              placeholder="Inboxing organization ID"
              rootClassName="mt-2"
              className="input-sm !h-10"
              data-testid="admin-pull-org-id"
            />
          </div>
        )}

        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <TextField
            value={search}
            onChange={({ target: { value } }) => onSearchChange(value)}
            placeholder="Search domains or tags"
            startIcon={<SearchIcon className="h-5 w-5" />}
            rootClassName="flex-1"
            className="input-sm !h-10"
            data-testid="add-domain-search"
          />
          <OutlineButton
            type="button"
            className="btn-sm bg-white text-black"
            disabled={syncLoading || loading}
            loading={syncLoading}
            onClick={onRefresh}
            data-testid="sync-inboxing-domains"
          >
            {showAdminPull && adminPullOrgId.trim()
              ? "Pull from Inboxing"
              : "Refresh from Inboxing"}
          </OutlineButton>
        </div>

        {allTags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {allTags.map((tag) => {
              const selected = selectedTags.includes(tag)
              return (
                <button
                  key={tag}
                  type="button"
                  className={cx(
                    "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                    selected
                      ? "border-primary bg-primary-glass text-primary"
                      : "border-outline bg-input text-black hover:border-outline-dark",
                  )}
                  onClick={() => onTagToggle(tag)}
                  data-testid={`tag-filter-${testIdSegment(tag)}`}
                >
                  {tag}
                </button>
              )
            })}
          </div>
        )}

        <div className="rounded-md border border-outline bg-white">
          {filteredDomains.length ? (
            <div className="max-h-[320px] divide-y divide-outline overflow-y-auto">
              {filteredDomains.map((domain) => {
                const checked = selectedDomainIds.includes(domain._id)
                return (
                  <label
                    key={domain._id}
                    className="flex cursor-pointer items-start gap-3 px-4 py-3 hover:bg-bg-default"
                  >
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 accent-primary"
                      checked={checked}
                      onChange={({ target: { checked } }) =>
                        setSelected(domain._id, checked)
                      }
                      disabled={loading}
                      data-testid="domain-select"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{domain.name}</div>
                      <div className="mt-1 flex flex-wrap gap-2 text-xs text-text-light">
                        <span>
                          {humanize(domain.inboxingStatus, "Available")}
                        </span>
                        <span>
                          {pluralize(mailboxCount(domain), "mailbox")}
                        </span>
                        {(domain.tags || []).map((tag) => (
                          <span
                            key={tag}
                            className="rounded-full bg-input px-2 py-0.5 text-black-secondary"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </label>
                )
              })}
            </div>
          ) : (
            <div className="flex min-h-[180px] items-center justify-center p-6">
              <EmptyList message="No domains match this search" />
            </div>
          )}
        </div>

        {selectedDomains.length > 0 && (
          <div className="text-sm text-text-light">
            Selected: {selectedDomains.map((domain) => domain.name).join(", ")}
          </div>
        )}
      </div>
    </Modal>
  )
}

type DomainRowProps = {
  domain: Domain
  showMailboxDetails: boolean
}

const DomainRow = ({ domain, showMailboxDetails }: DomainRowProps) => {
  const [open, setOpen] = useState(false)
  const mailboxes = domain.mailboxes || EMPTY_MAILBOXES
  const capacity = domainDailyCapacity(domain)
  const readyMailboxCount = domain.readyMailboxCount ?? 0
  const placementBadges = SANDMAN_PROVIDER_OPTIONS.flatMap(({ key, label }) => {
    const state = domain.placement?.[key]
    return state && PLACEMENT_BADGE_STATES.has(state)
      ? [{ provider: label, key, state }]
      : []
  })

  return (
    <div className="bg-white" data-testid={`domain-row-${domain._id}`}>
      <button
        type="button"
        className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left hover:bg-bg-default"
        onClick={() => showMailboxDetails && setOpen((value) => !value)}
        data-testid="domain-toggle"
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate font-semibold">{domain.name}</span>
            <StatusPill>Enabled</StatusPill>
            {placementBadges.map(({ key, provider, state }) => (
              <PlacementBadge
                key={key}
                provider={provider}
                state={state}
                domainId={domain._id}
              />
            ))}
          </div>
          <div className="mt-1 text-sm text-text-light">
            {pluralize(mailboxCount(domain), "mailbox")}
            {readyMailboxCount > 0
              ? ` · ${pluralize(
                  readyMailboxCount,
                  "ready mailbox",
                  "ready mailboxes",
                )}`
              : ""}
            {capacity !== undefined
              ? ` · ${capacity.toLocaleString()}/day available`
              : ""}
            {domain.csvIngestedAt ? " · Mailboxes imported" : ""}
          </div>
        </div>
        {showMailboxDetails && (
          <span className="shrink-0 text-sm text-primary">
            {open ? "Hide inboxes" : "View inboxes"}
          </span>
        )}
      </button>

      {showMailboxDetails && open && (
        <div className="border-t border-outline">
          {mailboxes.length ? (
            <div className="max-h-[360px] overflow-auto">
              <table className="w-full min-w-[600px] text-left text-sm">
                <thead className="bg-bg-default text-xs uppercase text-text-light">
                  <tr>
                    <th className="px-4 py-3 font-medium">Inbox</th>
                    <th className="px-4 py-3 font-medium">Display name</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Daily capacity</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline bg-white">
                  {mailboxes.map((mailbox) => (
                    <tr key={mailbox._id}>
                      <td className="px-4 py-3 font-medium">{mailbox.email}</td>
                      <td className="px-4 py-3">
                        {mailbox.displayName || "Managed automatically"}
                      </td>
                      <td className="px-4 py-3">{mailboxStatus(mailbox)}</td>
                      <td className="px-4 py-3">
                        {mailboxDailyCapacity(mailbox)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex min-h-[160px] items-center justify-center">
              <EmptyList message="No imported email addresses yet" />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const InboxingDomainsPage: FC<Props> = ({ className }) => {
  usePageTitle("Domains")
  const { id: orgId = "" } = useParams()
  const { isAdmin } = useAuth()
  const [search, setSearch] = useState("")
  const [burnedOnly, setBurnedOnly] = useState(false)
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [modalSearch, setModalSearch] = useState("")
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [selectedDomainIds, setSelectedDomainIds] = useState<string[]>([])
  const [adminPullOrgId, setAdminPullOrgId] = useState("")
  const [pendingAction, setPendingAction] = useState<PendingAction>(null)
  const [actionError, setActionError] = useState<AlertDetails | undefined>()
  const [actionMessage, setActionMessage] = useState<string | undefined>()

  const {
    data: { items: domains, entitlements } = { items: [], total: 0 },
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetInboxingDomainsQuery({ orgId }, { skip: !orgId })
  const [syncInboxingDomains, { isLoading: isSyncing }] =
    useSyncInboxingDomainsMutation()
  const [enableInboxingDomain] = useEnableInboxingDomainMutation()
  const [addDomainsToFolder] = useAddDomainsToFolderMutation()
  const { hasEditPermissionForPage } = useOrganizationPermissions({
    page: "inboxing-domains",
  })
  const canEdit = hasEditPermissionForPage("inboxing-domains")

  const folderDomains = useMemo(
    () => domains.filter((domain) => domain.inFolder),
    [domains],
  )
  const availableDomains = useMemo(
    () =>
      domains.filter(
        (domain) =>
          !domain.inFolder &&
          // Only show active Inboxing domains (or ones already enabled for
          // DreamSender). Non-active domains (setting up, failed, deleted, etc.)
          // are hidden.
          ((domain.inboxingStatus || "").toLowerCase() === "active" ||
            domainIsEnabledForDreamSender(domain)),
      ),
    [domains],
  )
  const filteredFolderDomains = useMemo(() => {
    const term = search.toLowerCase().trim()
    return folderDomains.filter(
      (domain) =>
        (!burnedOnly || domainHasBurnedPlacement(domain)) &&
        (!term ||
          [domain.name, ...(domain.tags || [])]
            .map((value) => value.toLowerCase())
            .some((value) => value.includes(term))),
    )
  }, [burnedOnly, folderDomains, search])
  const clearActionState = () => {
    setActionError(undefined)
    setActionMessage(undefined)
  }

  const openAddModal = () => {
    clearActionState()
    setModalSearch("")
    setSelectedTags([])
    setSelectedDomainIds([])
    setAddModalOpen(true)
  }

  const closeAddModal = () => {
    if (pendingAction?.type === "enable") return
    setAddModalOpen(false)
  }

  const handleRefresh = async () => {
    if (!orgId) return
    clearActionState()
    setPendingAction({ type: "sync" })
    try {
      const targetInboxingOrgId =
        isAdmin && adminPullOrgId.trim() ? adminPullOrgId.trim() : undefined
      await syncInboxingDomains({ orgId, targetInboxingOrgId }).unwrap()
      setActionMessage(
        targetInboxingOrgId
          ? `Domains pulled from Inboxing org ${targetInboxingOrgId}.`
          : "Domain list refreshed from Inboxing.",
      )
      await refetch()
    } catch (error) {
      setActionError(
        describeApiError(error as FetchBaseQueryError | SerializedError),
      )
    } finally {
      setPendingAction(null)
    }
  }

  const handleAddDomains = async () => {
    if (!orgId || !selectedDomainIds.length) return
    clearActionState()
    setPendingAction({ type: "enable" })
    try {
      const selected = availableDomains.filter((domain) =>
        selectedDomainIds.includes(domain._id),
      )
      const notEnabled = selected.filter(
        (domain) => !domainIsEnabledForDreamSender(domain),
      )
      const alreadyEnabled = selected.filter(domainIsEnabledForDreamSender)
      for (const domain of notEnabled) {
        await enableInboxingDomain({
          orgId,
          domainId: domain._id,
          importCsv: true,
          importMailboxes: true,
        }).unwrap()
      }
      if (alreadyEnabled.length) {
        await addDomainsToFolder({
          orgId,
          domainIds: alreadyEnabled.map((domain) => domain._id),
        }).unwrap()
      }
      setActionMessage(
        `${pluralize(
          selectedDomainIds.length,
          "domain",
        )} added to this folder.`,
      )
      setAddModalOpen(false)
      setSelectedDomainIds([])
      await refetch()
    } catch (error) {
      setActionError(
        describeApiError(error as FetchBaseQueryError | SerializedError),
      )
    } finally {
      setPendingAction(null)
    }
  }

  const handleTagToggle = (tag: string) => {
    setSelectedTags((tags) =>
      tags.includes(tag) ? tags.filter((item) => item !== tag) : [...tags, tag],
    )
  }

  if (isError) return <ErrorMessage />

  return (
    <div className={cx("flex h-full flex-col", className)}>
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <TextField
            value={search}
            onChange={({ target: { value } }) => setSearch(value)}
            placeholder="Search enabled domains"
            startIcon={<SearchIcon className="h-6 w-6" />}
            className="text-sm outline outline-outline !h-9 !py-1.5"
            rootClassName="basis-72"
          />
          <OutlineButton
            type="button"
            active={burnedOnly}
            onClick={() => setBurnedOnly((value) => !value)}
            className="btn-sm !h-9 !py-1.5 text-black"
            data-testid="burned-filter"
            aria-pressed={burnedOnly}
          >
            Burned
          </OutlineButton>
        </div>
        {canEdit && (
          <Button
            type="button"
            onClick={openAddModal}
            className="btn-primary btn-sm !h-9 !py-1.5"
            data-testid="open-add-domain-modal"
          >
            <AddIcon />
            <span>Add Domain</span>
          </Button>
        )}
      </div>

      {(actionError || actionMessage) && (
        <div className="mt-4">
          {actionError ? (
            <Alert details={actionError} />
          ) : (
            <Alert
              details={{
                title: "Action complete",
                message: actionMessage || "Done.",
                tone: "success",
              }}
            />
          )}
        </div>
      )}

      <div
        className={cx("mt-5 flex min-h-[380px] flex-1 overflow-y-auto", {
          "items-center justify-center": !filteredFolderDomains.length,
        })}
      >
        {isLoading || isFetching ? (
          <Loader />
        ) : filteredFolderDomains.length ? (
          <div className="w-full overflow-hidden rounded-md border border-outline bg-white shadow-[0px_1px_4px_0px_rgba(0,0,0,0.04)]">
            {filteredFolderDomains.map((domain) => (
              <DomainRow
                key={domain._id}
                domain={domain}
                showMailboxDetails={Boolean(isAdmin)}
              />
            ))}
          </div>
        ) : (
          <EmptyList
            message={
              folderDomains.length
                ? "No domains match this search"
                : "No domains added to this folder yet"
            }
          >
            {canEdit && (
              <Button
                type="button"
                onClick={openAddModal}
                className="btn-sm group bg-white hover:bg-primary border !border-solid border-outline-dark hover:border-primary"
              >
                <AddIcon className="text-primary transition-colors group-hover:text-white" />
                <span className="font-medium text-primary transition-colors group-hover:text-white">
                  Add Domain
                </span>
              </Button>
            )}
          </EmptyList>
        )}
      </div>

      <AddDomainModal
        open={addModalOpen}
        domains={availableDomains}
        entitlements={entitlements}
        selectedDomainIds={selectedDomainIds}
        search={modalSearch}
        selectedTags={selectedTags}
        loading={pendingAction?.type === "enable"}
        syncLoading={isSyncing || pendingAction?.type === "sync"}
        onClose={closeAddModal}
        onConfirm={handleAddDomains}
        onSearchChange={setModalSearch}
        onSelectedDomainIdsChange={setSelectedDomainIds}
        onTagToggle={handleTagToggle}
        onRefresh={handleRefresh}
        showAdminPull={Boolean(isAdmin)}
        adminPullOrgId={adminPullOrgId}
        onAdminPullOrgIdChange={setAdminPullOrgId}
      />
    </div>
  )
}

export default InboxingDomainsPage
